import { renderTemplate } from '../../../lib/outreach/merge'
import type { MergeContext } from '../../../lib/outreach/merge'
import type { CustomFieldDef } from '../../../lib/outreach/types'

export interface SampleContact {
  first_name: string
  company: string
  site: string
  custom: Record<string, string>
}

export const DEFAULT_SAMPLE: SampleContact = {
  first_name: 'Alex',
  company: 'Example Ltd',
  site: 'example.com',
  custom: {},
}

interface Props {
  stepNo: number | null
  subject: string
  body: string
  sample: SampleContact
  onSample: (next: SampleContact) => void
  defs: readonly CustomFieldDef[]
}

export function StepPreview({ stepNo, subject, body, sample, onSample, defs }: Props) {
  const keys = defs.map((d) => d.key)
  const ctx: MergeContext = { ...sample }
  const subj = renderTemplate(subject, ctx, keys)
  const main = renderTemplate(body, ctx, keys)
  const empty = [...new Set([...subj.empty, ...main.empty])]

  const base: ReadonlyArray<{ key: 'first_name' | 'company' | 'site'; label: string }> = [
    { key: 'first_name', label: 'Sample first name' },
    { key: 'company', label: 'Sample company' },
    { key: 'site', label: 'Sample site' },
  ]

  return (
    <section className="os-preview" aria-label="Preview">
      <strong className="os-name">{stepNo ? `Preview of step ${stepNo}` : 'Preview'}</strong>
      <div className="os-grid">
        {base.map((f) => (
          <div className="settings-row os-field" key={f.key}>
            <label className="settings-label" htmlFor={`sample-${f.key}`}>
              {f.label}
            </label>
            <input
              id={`sample-${f.key}`}
              className="settings-input"
              value={sample[f.key]}
              onChange={(e) => onSample({ ...sample, [f.key]: e.target.value })}
            />
          </div>
        ))}
        {defs.map((d) => (
          <div className="settings-row os-field" key={d.key}>
            <label className="settings-label" htmlFor={`sample-custom-${d.key}`}>
              Sample {d.label}
            </label>
            <input
              id={`sample-custom-${d.key}`}
              className="settings-input"
              value={sample.custom[d.key] ?? ''}
              onChange={(e) =>
                onSample({ ...sample, custom: { ...sample.custom, [d.key]: e.target.value } })
              }
            />
          </div>
        ))}
      </div>
      <div className="os-preview-subject" data-testid="preview-subject">
        {subj.text || 'No subject'}
      </div>
      <div className="os-preview-body" data-testid="preview-body">
        {main.text || 'No body'}
      </div>
      {empty.length > 0 && (
        <div className="os-warn">No value for: {empty.map((f) => `{{${f}}}`).join(', ')}</div>
      )}
    </section>
  )
}
